import React from 'react'
import AccentReadOnlyCheckbox from '../../../../../common/FormFields/AccentReadOnlyCheckbox'
import HtmlOrPlainShortDescription from './HtmlOrPlainShortDescription'
import { getSectionDisplayOrdinal } from '../../utils/sectionDisplayOrdinal'

const Section = ({
  index,
  section,
  sectionDisplayOrdinal: ordinalProp,
  children,
  showCheckbox,
  isChecked,
  onCheckboxChange,
  hideSectionHeader = false,
  disabled = false,
  readOnlyCheckboxTitleMatch: _readOnlyCheckboxTitleMatch = null,
  breadcrumbText = null,
}) => {
  // Support both API format (version_details.version_content) and snapshot format (detail)
  const sectionContent = section?.version_details?.version_content || section?.detail || {};
  
  // Extract section number from breadcrumb text if available (this has the correct audit trail info)
  const getSectionOrdinalFromBreadcrumb = () => {
    // Use the passed breadcrumbText prop first (from RevisionPreview), then fall back to section content
    const breadcrumb = breadcrumbText || sectionContent.breadcrumb_text;
    if (breadcrumb && typeof breadcrumb === 'string') {
      const breadcrumbSectionMatch = /Section\s+(\d+)/i.exec(breadcrumb.split(' > ').pop());
      if (breadcrumbSectionMatch) {
        const ordinal = parseInt(breadcrumbSectionMatch[1], 10);
        return ordinal;
      }
    }
    return null;
  };
  
  // Header "Section N": parent can pass ordinal (with full sibling list for 0-based sibling_order); else infer
  // Prioritize breadcrumb information as it contains the correct audit trail section info
  const breadcrumbOrdinal = getSectionOrdinalFromBreadcrumb();
  const sectionDisplayOrdinal =
    breadcrumbOrdinal != null
      ? breadcrumbOrdinal
      : ordinalProp != null && ordinalProp !== undefined
      ? ordinalProp
      : getSectionDisplayOrdinal(section, index);
  const sectionHeaderTitle = `Section ${sectionDisplayOrdinal}`;
  
  // Content below header: section name (e.g. "Section 1 for VH") and short description
  const sectionName =
    section.detail?.section_name ||
    sectionContent.section_name ||
    section.section_name ||
    (sectionContent.breadcrumb_text ? sectionContent.breadcrumb_text.split(' > ').pop() : null) ||
    sectionContent.pageTitle ||
    "";
  const shortDescription = sectionContent.short_description || section.short_description || section.detail?.short_description || "";
  const sectionId =
    section.tree_node_id ||
    sectionContent.section_tree_node_id ||
    sectionContent.section_id_ref ||
    section.section_tree_node_id;

  const handleCheckboxChange = () => {
    if (onCheckboxChange && sectionId) {
      onCheckboxChange(sectionId);
    }
  };

  /**
   * Read-only audit tree: show the same accent halo as Step (AccentReadOnlyCheckbox) whenever
   * this section is checked. Requiring title-match ordinal === sectionDisplayOrdinal failed when
   * the app bar said "Section 2" but sibling ordering / names made this row "Section 1".
   */
  const emphasizeReadOnlyCheckbox =
    disabled && !!(isChecked || false);

  // If hideSectionHeader is true, only render children without section header
  if (hideSectionHeader) {
    return <div>{children}</div>;
  }

  return (
    <>
      <div style={{ marginBottom: '25px' }} className="header-spacing">
        {/* Header row: checkbox + "Section 1" (bold) - same as SectionNode */}
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "10px",
          }}
        >
          {showCheckbox &&
            (disabled ? (
              <AccentReadOnlyCheckbox
                readOnlyVisual
                emphasizeChecked={emphasizeReadOnlyCheckbox}
                checked={isChecked || false}
              />
            ) : (
              <input
                type="checkbox"
                checked={isChecked || false}
                onChange={handleCheckboxChange}
                style={{
                  width: "18px",
                  height: "18px",
                  cursor: "pointer",
                  flexShrink: 0,
                }}
              />
            ))}
          <h2
            style={{
              fontSize: "20px",
              margin: 0,
              fontWeight: 700,
              lineHeight: 1.25,
            }}
          >
            {sectionHeaderTitle}
          </h2>
        </div>
        {/* Two lines (double hr) below header - same as SectionNode */}
        <hr />
        <hr />
        {/* Section name and short description - 2 lines in one block like SectionNode */}
        <div style={{ marginBottom: '16px' }}>
          {sectionName && <div>{sectionName}</div>}
          <HtmlOrPlainShortDescription value={shortDescription} />
        </div>
        <div>{children}</div>
      </div>
    </>
  );
}

export default Section